import { PayloadAction, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  GridActionArray,
  GridActionNumeric,
  GridActionSearch,
  GridColumnState,
  GridState,
} from '@dmc/ui/tables';

interface StoredGridState extends Pick<GridState, 'id' | 'columnState'> {}

// Type guard for shallow validation of local storage data.
function isStoredGridState(data: unknown): data is StoredGridState {
  // This type guard could be improved to validate array item shape.
  return (
    data instanceof Object &&
    'id' in data &&
    typeof data.id === 'string' &&
    'columnState' in data &&
    Array.isArray(data.columnState)
  );
}

const GRID_STORAGE_PREFIX = 'grid';

export const ASSETS_HOME_SLICE_KEY = 'AssetsOverview';
export const ASSETS_PAGE_SLICE_KEY = 'AssetsPage';
export const GEOFENCES_SLICE_KEY = 'GeofencesOverview';

const SLICE_KEYS_TO_PERSIST: string[] = [
  ASSETS_PAGE_SLICE_KEY,
  ASSETS_HOME_SLICE_KEY,
  GEOFENCES_SLICE_KEY,
];

const getGridStorageKey = (sliceKey: string) => `${GRID_STORAGE_PREFIX}-${sliceKey}`;

const gridAdapter = createEntityAdapter<GridState>();

const getPersistedData = (): GridState[] => {
  const keys = Object.keys(window.localStorage).filter(k => k.startsWith(GRID_STORAGE_PREFIX));
  const gridData: GridState[] = [];

  keys.forEach(k => {
    try {
      const item = window.localStorage.getItem(k);
      const data = item ? JSON.parse(item) : undefined;

      if (data && isStoredGridState(data) && SLICE_KEYS_TO_PERSIST.includes(data.id)) {
        // Create a full GridState object from the partial StoredGridState.
        gridData.push({
          id: data.id,
          columnState: data.columnState,
          firstVisibleRow: 0,
          selectedRows: [],
          search: undefined,
        });
      }
    } catch {
      // Swallow
    }
  });

  return gridData;
};

const writePersistedData = (payload: StoredGridState) => {
  try {
    const key = getGridStorageKey(payload.id);
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Swallow
  }
};

const gridSlice = createSlice({
  name: 'grid',
  initialState: gridAdapter.getInitialState(undefined, getPersistedData() ?? []),
  reducers: {
    register: (state, action: PayloadAction<string>) => {
      gridAdapter.addOne(state, {
        id: action.payload,
        firstVisibleRow: 0,
        columnState: [],
        selectedRows: [],
      });
    },
    setSearch: (state, action: PayloadAction<GridActionSearch>) => {
      gridAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          search: action.payload.search,
        },
      });
    },
    setFirstVisibleRow: (state, action: PayloadAction<GridActionNumeric>) => {
      gridAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          firstVisibleRow: action.payload.value,
        },
      });
    },
    setColumnState: (state, action: PayloadAction<GridActionArray<GridColumnState>>) => {
      gridAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          columnState: action.payload.value,
        },
      });

      // Save the column state of specific sliceKeys to local storage.
      if (SLICE_KEYS_TO_PERSIST.includes(action.payload.id)) {
        writePersistedData({
          id: action.payload.id,
          columnState: action.payload.value,
        });
      }
    },
    setSelectedRows: (state, action: PayloadAction<GridActionArray<string>>) => {
      gridAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          selectedRows: action.payload.value,
        },
      });
    },
    resetTable: (state, action: PayloadAction<string>) => {
      gridAdapter.updateOne(state, {
        id: action.payload,
        changes: {
          firstVisibleRow: 0,
          columnState: [],
          selectedRows: [],
          search: undefined,
        },
      });
    },
  },
});

export const { selectById: selectGridData } = gridAdapter.getSelectors<RootState>(
  state => state.grid,
);

export const {
  register,
  setSearch,
  setFirstVisibleRow,
  setColumnState,
  setSelectedRows,
  resetTable,
} = gridSlice.actions;

export default gridSlice.reducer;
